import { useState, useEffect } from 'react';
import { api, Vendor, CreateVendorData, UpdateVendorData } from '../api';
import Layout from '../components/Layout';

function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="card p-6 w-full max-w-lg relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminVendors() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [formData, setFormData] = useState<CreateVendorData>({
    vendor_id: '',
    name: '',
    status: 'active',
    ships_for_us: 1,
    shipping_cost_type: 'quote',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const { vendors } = await api.getVendors();
      setVendors(vendors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingVendor(null);
    setFormData({
      vendor_id: '',
      name: '',
      status: 'active',
      ships_for_us: 1,
      shipping_cost_type: 'quote',
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendor_id: vendor.vendor_id,
      name: vendor.name,
      status: vendor.status,
      ships_for_us: vendor.ships_for_us,
      shipping_origin_country: vendor.shipping_origin_country || undefined,
      shipping_origin_state: vendor.shipping_origin_state || undefined,
      production_lead_time_days: vendor.production_lead_time_days || undefined,
      shipping_lead_time_days: vendor.shipping_lead_time_days || undefined,
      shipping_cost_type: vendor.shipping_cost_type,
      shipping_cost_flat: vendor.shipping_cost_flat || undefined,
      contact_email: vendor.contact_email || undefined,
      notes: vendor.notes || undefined,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      if (editingVendor) {
        const updateData: UpdateVendorData = {
          name: formData.name,
          status: formData.status,
          ships_for_us: formData.ships_for_us,
          shipping_origin_country: formData.shipping_origin_country,
          shipping_origin_state: formData.shipping_origin_state,
          production_lead_time_days: formData.production_lead_time_days,
          shipping_lead_time_days: formData.shipping_lead_time_days,
          shipping_cost_type: formData.shipping_cost_type,
          shipping_cost_flat: formData.shipping_cost_flat,
          contact_email: formData.contact_email,
          notes: formData.notes,
        };
        await api.updateVendor(editingVendor.id, updateData);
      } else {
        await api.createVendor(formData);
      }
      setShowModal(false);
      loadVendors();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save vendor');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleStatus = async (vendor: Vendor) => {
    try {
      await api.updateVendor(vendor.id, {
        status: vendor.status === 'active' ? 'inactive' : 'active',
      });
      loadVendors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update vendor');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Vendors</h1>
            <p className="text-ink-400 mt-1">Manage your vendor partners</p>
          </div>
          <button onClick={openCreateModal} className="btn-primary">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Vendor
          </button>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Vendors Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Ships For Us
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Lead Times
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800">
                {vendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-ink-400">
                      No vendors yet. Click "Add Vendor" to create your first one.
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-ink-800/50 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-ink-100">{vendor.name}</div>
                          {vendor.contact_email && (
                            <div className="text-sm text-ink-400">{vendor.contact_email}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-sm text-accent-400 bg-ink-800 px-2 py-1 rounded">
                          {vendor.vendor_id}
                        </code>
                      </td>
                      <td className="px-4 py-4">
                        {vendor.ships_for_us ? (
                          <span className="status-badge status-badge-active">Yes</span>
                        ) : (
                          <span className="status-badge status-badge-inactive">No</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-ink-300">
                        {vendor.production_lead_time_days || vendor.shipping_lead_time_days ? (
                          <div>
                            {vendor.production_lead_time_days && (
                              <div>Prod: {vendor.production_lead_time_days}d</div>
                            )}
                            {vendor.shipping_lead_time_days && (
                              <div>Ship: {vendor.shipping_lead_time_days}d</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-ink-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`status-badge ${
                            vendor.status === 'active'
                              ? 'status-badge-active'
                              : 'status-badge-inactive'
                          }`}
                        >
                          {vendor.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(vendor)}
                            className="btn-ghost text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleStatus(vendor)}
                            className="btn-ghost text-sm"
                          >
                            {vendor.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-3 text-accent-400 text-sm">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Vendor ID *</label>
              <input
                type="text"
                className="input w-full"
                value={formData.vendor_id}
                onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                placeholder="la-splits"
                required
                disabled={!!editingVendor}
              />
              <p className="text-xs text-ink-500 mt-1">Unique identifier (cannot change later)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Name *</label>
              <input
                type="text"
                className="input w-full"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="LA Splits"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Contact Email</label>
              <input
                type="email"
                className="input w-full"
                value={formData.contact_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="vendor@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Status</label>
              <select
                className="input w-full"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="border-t border-ink-800 pt-4 mt-4">
            <h3 className="text-sm font-medium text-ink-200 mb-3">Shipping Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Ships For Us</label>
                <select
                  className="input w-full"
                  value={formData.ships_for_us}
                  onChange={(e) => setFormData({ ...formData, ships_for_us: parseInt(e.target.value) })}
                >
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Shipping Cost Type</label>
                <select
                  className="input w-full"
                  value={formData.shipping_cost_type}
                  onChange={(e) => setFormData({ ...formData, shipping_cost_type: e.target.value as any })}
                >
                  <option value="quote">Quote</option>
                  <option value="flat">Flat Rate</option>
                  <option value="per_item">Per Item</option>
                  <option value="included">Included</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Production Lead Time (days)</label>
              <input
                type="number"
                className="input w-full"
                value={formData.production_lead_time_days || ''}
                onChange={(e) => setFormData({ ...formData, production_lead_time_days: parseInt(e.target.value) || undefined })}
                placeholder="7"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Shipping Lead Time (days)</label>
              <input
                type="number"
                className="input w-full"
                value={formData.shipping_lead_time_days || ''}
                onChange={(e) => setFormData({ ...formData, shipping_lead_time_days: parseInt(e.target.value) || undefined })}
                placeholder="3"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Notes (Admin Only)</label>
            <textarea
              className="input w-full"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Internal notes about this vendor..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={formLoading} className="btn-primary">
              {formLoading ? 'Saving...' : editingVendor ? 'Save Changes' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}
